import { Controller } from 'stimulus';

const SPACE_BETWEEN_CIRCLES = 30;
const TRANSITION_DURATION = 800;
const OVERLAY_TRANSITION_DURATION = 300;

class Circles extends Controller {
  connect() {
    this.activeCircle = null;

    this.storeDimensions();
  }

  storeDimensions() {
    this.containerWidth = this.element.offsetWidth;
    this.containerHeight = this.element.offsetHeight;
  }

  onWindowResize() {
    this.storeDimensions();
  }

  onCloseClick() {
    this.resetCircleOverlays().then(() => {
      this.resetCirclesPositions();
      this.activeCircle = null;

      setTimeout(() => {
        this.setIsHoverable(true);
      }, TRANSITION_DURATION);
    });
  }

  onCircleClick(event) {
    const { currentTarget } = event;

    this.setIsHoverable(false);

    this.resetCircleOverlays()
      .then(() => {
        this.resetCirclesPositions();

        if (currentTarget === this.activeCircle) {
          this.activeCircle = null;

          return;
        }

        this.activeCircle = currentTarget;

        this.positionCircles();

        this.scrollToActiveCircle();

        setTimeout(() => {
          this.showCircleOverlay(currentTarget.getAttribute('data-target-index'));
        }, TRANSITION_DURATION);
      });
  }

  setIsHoverable(state) {
    this.circleTargets.forEach((circle) => {
      circle.classList[state ? 'add' : 'remove']('is-hoverable');
    });
  }

  resetCircleOverlays() {
    return new Promise((resolve) => {
      if (!this.activeCircle) {
        resolve();
        return;
      }

      this.circleOverlayTargets.forEach((circleOverlay) => {
        circleOverlay.style.opacity = 0;
        circleOverlay.style.visibility = 'hidden';
      });

      setTimeout(resolve, OVERLAY_TRANSITION_DURATION);
    });
  }

  resetCirclesPositions() {
    this.circleTargets.forEach((circle) => {
      circle.removeAttribute('style');
    });

    this.circleInnerTargets.forEach((circleInner) => {
      circleInner.style.opacity = 1;
    });
  }

  showCircleOverlay(index) {
    const target = this.circleOverlayTargets[index];

    target.style.opacity = 1;
    target.style.visibility = 'visible';
  }

  positionCircles() {
    const index = this.circleTargets.indexOf(this.activeCircle);

    const { offsetHeight, offsetTop, offsetLeft } = this.activeCircle;
    const { containerWidth, containerHeight } = this;

    // We've to move the circle up to the 0 point on the Y axis...
    const translateYDelta = offsetTop * -1;

    // We've to move the circle to the center on the X axis...
    const translateXDelta = ((containerWidth / 2) - (containerHeight / 2)) - offsetLeft;

    // Scale the circle to cover the height of the container...
    const scaleDelta = containerHeight / offsetHeight;

    // Calculate the left and right bounds for the active circle after the scaling...
    const mainCircleLeftBound = ((containerWidth / 2) - (containerHeight / 2));
    const mainCircleRightBound = mainCircleLeftBound + containerHeight;

    // Set start index for forward and backward loop. -1 and +1 are to exclude the
    // active circle...
    let backwardLoopIndex = index - 1;
    let forwardLoopIndex = index + 1;

    let startBackwardBound = mainCircleLeftBound - SPACE_BETWEEN_CIRCLES;
    let startForwardBound = mainCircleRightBound;

    // Start looping backward starting at the selected element...
    while (backwardLoopIndex >= 0) {
      const element = this.circleTargets[backwardLoopIndex];

      const { offsetWidth: elementOffsetWidth, offsetLeft: elementOffsetLeft } = element;

      // Calculate where we're supposed to place the circle in the current loop...
      const newOffsetLeft = startBackwardBound - elementOffsetWidth;

      // Calculate the difference between where it's supposed to go and the current position...
      const elementTranslateX = newOffsetLeft - elementOffsetLeft;

      // Move the circle...
      element.style.transform = `translateX(${elementTranslateX}px)`;

      element.style.opacity = 0.2;

      // Set the left bound for the next loop...
      startBackwardBound -= (elementOffsetWidth + SPACE_BETWEEN_CIRCLES);

      backwardLoopIndex -= 1;
    }

    while (forwardLoopIndex < this.circleTargets.length) {
      const element = this.circleTargets[forwardLoopIndex];

      const { offsetWidth: elementOffsetWidth, offsetLeft: elementOffsetLeft } = element;

      // Calculate where we're supposed to place the circle in the current loop...
      const newOffsetLeft = startForwardBound + SPACE_BETWEEN_CIRCLES;

      // Calculate the difference between where it's supposed to go and the current position...
      const elementTranslateX = newOffsetLeft - elementOffsetLeft;

      // Move the circle...
      element.style.transform = `translateX(${elementTranslateX}px)`;

      element.style.opacity = 0.2;

      // Set the left bound for the next loop...
      startForwardBound += (elementOffsetWidth + SPACE_BETWEEN_CIRCLES);

      forwardLoopIndex += 1;
    }

    // Move the active circle...
    this.activeCircle.style.transform = `translate(${translateXDelta}px, ${translateYDelta}px) scale(${scaleDelta})`;

    this.activeCircle.querySelector('.js-circle-inner').style.opacity = 0;
  }

  scrollToActiveCircle() {
    const scrollWrap = this.element.parentNode;

    // There is no scroll...
    if (scrollWrap.scrollWidth <= scrollWrap.offsetWidth) {
      return;
    }

    scrollWrap.scrollTo({
      left: (this.containerWidth / 2) - (this.containerHeight / 2),
      top: 0,
      behavior: 'smooth',
    });
  }
}

Circles.targets = [
  'circle',
  'circleInner',
  'circleOverlay',
];

export default Circles;
